import { Emptiable, isEmpty } from "@/specs/validation";
import assert from "assert";
import { Enum } from "./enums";

export const stringifyReplacer = (key: any, _: any) => typeof _ === 'bigint' ? _.toString() : _;

export function enrich<T extends object>(_: T, enricher: (t: T) => Partial<T>) {
  if (enricher === undefined) throw new Error("Parameter enricher must be defined.");
  if (enricher === null     ) throw new Error("Parameter enricher must be defined.");
  if (_        === undefined) throw new Error("Parameter _ must be defined.");
  if (_        === null     ) throw new Error("Parameter _ must be defined.");

  return {
    ..._,
    ...enricher(_),
  }
}
export function generateIfEmpty<T extends Emptiable>(_: T, generator: () => T): T {
  if (generator === undefined) throw new Error("Parameter generator must be defined.");
  if (generator === null     ) throw new Error("Parameter generator must be defined.");

  return !isEmpty(_) ? _ : generator();
}

export function dump<T>(_: T, message?: string): T {
  console.log(message ?? "DUMP =>", JSON.stringify(_, stringifyReplacer, 2));
  return _;
}

export function unexpectedCodePath(message?: string): never {
  throw new Error(`Unexpected code path! ${message ?? ""}`.trimEnd( ))
}
export function error(message?: string): never {
  throw new Error(message ?? "An error has occurred.");
}

export function map<T, U>(_: T, mapper: (t: T) => U): U {
  if (mapper === undefined) throw new Error("Parameter mapper must be defined.");
  if (mapper === null     ) throw new Error("Parameter mapper must be defined.");
  if (_      === undefined) throw new Error("Parameter _ must be defined.");
  if (_      === null     ) throw new Error("Parameter _ must be defined.");

  return mapper(_);
}

export const single = <T>(collection: T[], predicate: (item: T) => boolean, { message }: { message?: string } = {}): T => {
  const matches = collection.filter(predicate);
  const message_ = ({ reason }: { reason: string }) => message ? message : `Expected exactly one match - ${reason}`;
  assert(matches.length >   0, message_({ reason: "no matches found"       }));
  assert(matches.length === 1, message_({ reason: "multiple matches found" }));
  return matches[0];
}
export const singleOrUndefined = <T>(collection: T[], predicate: (item: T) => boolean, { message }: { message?: string } = {}): T | undefined => {
  const matches = collection.filter(predicate);
  const message_ = ({ reason }: { reason: string }) => message ? message : `Expected at most one match - ${reason}`;
  assert(matches.length <= 1, message_({ reason: "multiple matches found" }));
  return matches[0];
}
export const list = <T>(collection: T[], predicate: (item: T) => boolean): T[] => {
  const matches = collection.filter(predicate);
  return matches;
}

//#region native type helpers

export const isString   = (_: any): _ is string   => typeof _ === "string";
export const isNumber   = (_: any): _ is number   => typeof _ === "number" && !isNaN(_);
export const isBoolean  = (_: any): _ is boolean  => typeof _ === "boolean";
export const isFunction = (_: any): _ is Function => typeof _ === "function";

export const isArrayOf = <T>(predicate: (_: any) => _ is T) => (_: any): _ is T[] =>
  Array.isArray(_) && _.every(predicate);

export const isDate     = (_: any): _ is Date   => _ instanceof Date;
export const hasTimePart = (_: Date) => _.getUTCHours()        !== 0
                              || _.getUTCMinutes()      !== 0
                              || _.getUTCSeconds()      !== 0
                              || _.getUTCMilliseconds() !== 0;

export const isIntegerRange    = (_: number) => Number.isInteger(_) && _ >= -9007199254740991 && _ <= 9007199254740991;
export const isPercentageRange = isIntegerRange;

//#endregion
//#region helper types

export type Predicate<T> = (value: T) => boolean;
export type Mapper<TInput, TOutput> = (input: TInput) => TOutput;
export type Comparator<T> = (a: T, b: T) => number;
export const identity = <T extends unknown>(_: T) => _;


//#endregion
//#region Sorting helpers

export const ASC  = <T>(_: (a: T, b: T) => number) => (a: T, b: T) =>     _.call(null, a, b);
export const DESC = <T>(_: (a: T, b: T) => number) => (a: T, b: T) => 0 - _.call(null, a, b);

export const ORDER_BY = <T>(...comparators: Comparator<T>[]) => (a: T, b: T) => {
  assert(comparators.length > 0);
  for (const comparator of comparators) {
    const result = comparator(a, b);
    if (result !== 0) {
      return result;
    }
  }
  return 0;
}

//#endregion

export const integers = ({ from = 1, upTo }: {
  from?: number
  upTo:  number
}) => Array.from({ length: upTo - from + 1 }, (_, i) => i + from);

export const arrayAsRecord = <T>(array: T[]) => array.reduce((acc, item, index) => {
  acc[index.toString()] = item;
  return acc;
}, {} as Record<string, T>);


//#region Stringify

export function stringify(...args: (Enum | undefined)[]): string {
  function stringifyElement(arg: Enum | undefined): string {
    if (arg === undefined) return 'undefined';
    if ("identifier" in arg) return arg.identifier;
    unexpectedCodePath(`Unknown argument type: ${arg}`);
  }
  return args.map(stringifyElement).join('|');
}

//#endregion
